// features/onboarding/hooks/useJourneyContext.ts
import { useEffect, useState } from 'react';
import { useAppDispatch, useAppSelector } from 'app/hooks';
import { setJourneyContext, selectJourneyContext } from 'features/onboarding/reducers/onboardingSlice';
import { JOURNEY_CONFIGS, DEFAULT_JOURNEY } from 'features/frictionless-signup/config/journeyConfig';
import { signupApi } from 'features/frictionless-signup/services/signupApi';

/**
 * Journey context containing app information for the onboarding flow
 */
interface JourneyContext {
  /** Display name of the application (e.g., "Automated CI/CD") */
  app_name: string;
  /** Journey variant identifier (e.g., "cicd", "dbt") */
  app_variant: string;
  /** URL path to redirect to after onboarding (e.g., "/dynamic-delivery") */
  redirect_to: string;
}

/**
 * useJourneyContext Hook
 *
 * Manages journey context for the onboarding flow. Retrieves context from multiple sources
 * with intelligent fallback logic to ensure continuity from signup to onboarding.
 *
 * Journey Context Sources (in priority order):
 * 1. Redux persisted state: Journey context set during signup (from signupSlice)
 * 2. API status endpoint: Fetches app_variant from /api/v1/frictionless/website/status
 * 3. Default journey: Falls back to "Onboarding" if API returns 404 or no app_variant
 *
 * The hook automatically:
 * - First checks if context already exists in Redux (e.g., from signup flow)
 * - Only fetches from API if context is not already available
 * - Extracts app_variant from the API response
 * - Maps variant to full configuration (app_name, redirect_to)
 * - Stores context in Redux for app-wide access
 * - Provides fallback to default "Onboarding" journey
 *
 * @hook
 * @example
 * ```tsx
 * function OnboardingView() {
 *   const journeyContext = useJourneyContext();
 *
 *   if (!journeyContext) {
 *     return <LoadingView />;
 *   }
 *
 *   return <div>Welcome to {journeyContext.app_name}</div>;
 * }
 * ```
 *
 * @returns Journey context object or null if not yet loaded
 */
export const useJourneyContext = (): JourneyContext | null => {
  const dispatch = useAppDispatch();
  const existingContext = useAppSelector(selectJourneyContext);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    // If we already have context in Redux, don't fetch again
    if (existingContext !== null) {
      return;
    }

    const fetchJourneyContext = async (): Promise<void> => {
      if (isLoading) return;

      setIsLoading(true);
      try {
        // Fetch signup status from API
        const signupStatus = await signupApi.getCurrentUserSignupStatus();

        // Determine journey configuration based on app_variant from API
        let journeyConfig;
        if (signupStatus?.app_variant != null && signupStatus.app_variant in JOURNEY_CONFIGS) {
          journeyConfig = JOURNEY_CONFIGS[signupStatus.app_variant];
        } else {
          // Fallback to default "Onboarding" journey if 404 or no app_variant
          journeyConfig = JOURNEY_CONFIGS[DEFAULT_JOURNEY];
        }

        // Build complete journey context
        const context: JourneyContext = {
          app_name: journeyConfig.app_name,
          app_variant: journeyConfig.app_variant,
          redirect_to: journeyConfig.redirect_to,
        };

        // Store in Redux for access throughout onboarding flow
        dispatch(setJourneyContext(context));
      } catch (error) {
        console.error('[useJourneyContext] Error fetching journey context:', error);
        // Fall back to default journey on error
        const defaultConfig = JOURNEY_CONFIGS[DEFAULT_JOURNEY];
        const context: JourneyContext = {
          app_name: defaultConfig.app_name,
          app_variant: defaultConfig.app_variant,
          redirect_to: defaultConfig.redirect_to,
        };
        dispatch(setJourneyContext(context));
      } finally {
        setIsLoading(false);
      }
    };

    void fetchJourneyContext();
  }, [dispatch, existingContext, isLoading]);

  // Return current context from Redux (may be null during initial load)
  return existingContext;
};
